import { BadRequestError } from '../errors';

export interface ILeaderboardConfig {
  _id: string;
  name: string;
  order: number;
  startDate: Date;
  endDate?: Date;
  frequency: LeaderboardFrequency;
  organisation: string;
  gymName?: string;
  competitionType: LeaderboardCompetitionType;
  metric: LeaderboardMetric;
  teams?: string[];
  timezone: string;
  showInStudio: boolean;
  requireOptIn: boolean;
  countZeroScores?: boolean;
  includeImportedWorkouts?: boolean;
  capMinutes?: number;
}

export type LeaderboardFrequency = 'one-time' | 'daily' | 'weekly' | 'monthly';
export type LeaderboardCompetitionType = 'individual' | 'inter-team';
export type LeaderboardMetric =
  | 'effort-points'
  | 'total-minutes'
  | 'average-minutes'
  | 'team-weighted-minutes-score'
  | 'team-weighted-points-score';

export function createLeaderboardConfig(
  id: string,
  name: string,
  order: number,
  startDate: Date,
  frequency: LeaderboardFrequency,
  competitionType: LeaderboardCompetitionType,
  metric: LeaderboardMetric,
  timezone: string,
  organisation: string,
  gymName?: string,
  teams?: string[],
  endDate?: Date,
  showInStudio?: boolean,
  requireOptIn = false,
  countZeroScores?: boolean,
  includeImportedWorkouts?: boolean,
  capMinutes?: number,
): ILeaderboardConfig {
  const leaderboardConfig: ILeaderboardConfig = {
    _id: id,
    name,
    order,
    startDate,
    frequency,
    competitionType,
    metric,
    timezone,
    organisation,
    gymName,
    teams,
    endDate,
    showInStudio: showInStudio ?? false,
    requireOptIn,
    countZeroScores,
    includeImportedWorkouts: includeImportedWorkouts ?? true,
    capMinutes,
  };

  validate(leaderboardConfig);

  return leaderboardConfig;
}

function validate(leaderboardConfig: ILeaderboardConfig) {
  validateRequiredFields(leaderboardConfig);
  validateInputDates(leaderboardConfig);
  // TODO: add guard when requireOptIn is false for teams when opt in for teams is implemented
}

function validateRequiredFields(leaderboardConfig: ILeaderboardConfig) {
  if (!leaderboardConfig.name) {
    throw new BadRequestError({details: 'name field is required'});
  }

  if (!leaderboardConfig.order) {
    throw new BadRequestError({details: 'order field is required'});
  }

  if (!leaderboardConfig.startDate) {
    throw new BadRequestError({details: 'startDate field is required'});
  }

  if (!leaderboardConfig.organisation) {
    throw new BadRequestError({details: 'organisation field is required'});
  }

  if (!leaderboardConfig.competitionType) {
    throw new BadRequestError({details: 'competitionType field is required'});
  }

  if (!leaderboardConfig.metric) {
    throw new BadRequestError({details: 'metric field is required'});
  }

  if (!leaderboardConfig.timezone) {
    throw new BadRequestError({details: 'timezone field is required'});
  }

  if (!leaderboardConfig.frequency) {
    throw new BadRequestError({details: 'frequency field is required'});
  }
}

function validateInputDates(leaderboardConfig: ILeaderboardConfig) {
  if (leaderboardConfig.frequency === 'one-time' && !leaderboardConfig.endDate) {
    throw new BadRequestError({details: 'endDate field is required when leaderboard is one time'});
  }
}
